import useSeo from "@/lib/useSeo";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import MarketingShell from "@/components/marketing/MarketingShell";
import api, { errMsg } from "@/lib/api";
import { DIMS, LEVELS, BARRIER } from "@/components/app/readinessShared";

export default function ReadinessCheck() {
  useSeo({ title: "Free change readiness check", description: "A free 2-minute change readiness check across Awareness, Desire, Knowledge, Ability and Reinforcement — see where your change will stick and where it won’t, no sign-up needed.", path: "/readiness-check" });
  const [s, setS] = useState({ awareness: 3, desire: 3, knowledge: 3, ability: 3, reinforcement: 3 });
  const [title, setTitle] = useState("");
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [email, setEmail] = useState("");
  const [followUp, setFollowUp] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [emailErr, setEmailErr] = useState("");
  const run = async () => {
    setErr("");
    try { const { data } = await api.post("/public/readiness-check", s); setRes(data); setSent(false); setEmailErr(""); localStorage.setItem("changeos_carry", JSON.stringify({ title, sliders: s })); window.scrollTo({ top: 0, behavior: "smooth" }); }
    catch (e) { setErr(errMsg(e)); }
  };
  const sendResult = async () => {
    setEmailErr("");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { setEmailErr("Please enter a valid email address."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/public/readiness-check/email", { email: email.trim(), title, sliders: s, follow_up: followUp });
      if (data?.email_status === "sent") setSent(true);
      else setEmailErr("We couldn't send the email just now — your result is still on screen above. Please try again later.");
    }
    catch (e) { setEmailErr(errMsg(e)); }
    finally { setSending(false); }
  };
  return (
    <MarketingShell>
      <section className="pp-grid-bg border-b border-border-soft"><div className="max-w-6xl mx-auto px-6 pt-16 pb-12">
        <div className="pp-overline">Free · no sign-up</div>
        <h1 className="font-head font-extrabold tracking-tight text-4xl sm:text-5xl mt-3 max-w-3xl">2-minute change readiness check</h1>
        <p className="text-lg text-subtle mt-4 max-w-2xl">Five quick questions about the people affected by your change. You get the full scored result and what to fix first — no email required.</p>
      </div></section>
      <section className="max-w-6xl mx-auto px-6 py-14 grid lg:grid-cols-[1fr_.8fr] gap-8" data-testid="readiness-check-page">
        <div className="pp-card p-7">
          <label className="pp-label" htmlFor="rc-title">What's the change? (optional)</label>
          <input id="rc-title" data-testid="rc-title" className="pp-input mb-6" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Moving Finance onto the new expenses app" />
          <div className="space-y-5">
            {DIMS.map(([k, name, hint]) => (
              <div key={k} data-testid={`rc-${k}`}>
                <div className="flex items-center justify-between"><div className="font-bold text-foreground">{name}</div><div className="text-xs font-bold text-accent-700">{s[k]} · {LEVELS[s[k]]}</div></div>
                <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>
                <div className="flex gap-1.5 mt-2">{[1, 2, 3, 4, 5].map((v) => <button key={v} data-testid={`rc-${k}-${v}`} onClick={() => setS({ ...s, [k]: v })} className={`flex-1 h-9 rounded-lg text-xs font-bold border transition-colors ${s[k] >= v ? "bg-accent-500 border-accent-500 text-on-dark" : "bg-card border-border text-muted-foreground hover:border-accent-300"}`}>{v}</button>)}</div>
              </div>
            ))}
          </div>
          {err && <div className="mt-4 text-sm text-danger-700" data-testid="rc-error">{err}</div>}
          <button data-testid="rc-submit" onClick={run} className="pp-btn-dark w-full mt-7"><Sparkles size={16} /> Show my result</button>
        </div>
        <div className="space-y-4">
          {!res ? <div className="pp-card p-7 text-sm text-muted-foreground">Your scored result appears here — instantly, in full.</div> : (
            <>
              <div className="pp-card p-7 text-center" data-testid="rc-result">
                <div className="pp-label">Overall readiness · <span className="normal-case">Estimate</span></div>
                <div data-testid="rc-overall" className={`font-head text-6xl font-extrabold tracking-tight ${res.overall < 40 ? "text-danger-600" : res.overall < 65 ? "text-warning-600" : "text-brand-700"}`}>{res.overall}%</div>
                <div className="mt-5 space-y-2 text-left">{DIMS.map(([k, name]) => <div key={k} className="flex items-center gap-3 text-xs"><span className="w-28 font-semibold text-subtle">{name}</span><div className="flex-1 h-2.5 rounded bg-secondary"><div className="h-2.5 rounded bg-accent-500" style={{ width: `${res.scores[k]}%` }} /></div><span className="font-mono w-9 text-right">{res.scores[k]}%</span></div>)}</div>
              </div>
              <div className="pp-card p-6 bg-ink text-on-dark"><div className="text-xs font-bold uppercase tracking-widest text-brand-300">Fix this first · {DIMS.find((d) => d[0] === res.weakest)[1]}</div><p className="text-sm text-faint mt-2 leading-relaxed">{BARRIER[res.weakest]}</p></div>
              <div className="pp-card p-6" data-testid="rc-email-card">
                {sent ? (
                  <div data-testid="rc-email-sent" className="text-sm font-semibold text-brand-700">Sent — check your inbox.</div>
                ) : (
                  <>
                    <div className="font-head text-lg font-extrabold">Email me this result</div>
                    <label className="pp-label mt-3" htmlFor="rc-email">Your email</label>
                    <input id="rc-email" data-testid="rc-email-input" type="email" className="pp-input" value={email} onChange={(e) => setEmail(e.target.value)} />
                    <label className="flex items-start gap-2 mt-3 cursor-pointer">
                      <input id="rc-followup" data-testid="rc-email-followup" type="checkbox" className="mt-1" checked={followUp} onChange={(e) => setFollowUp(e.target.checked)} />
                      <span className="text-sm text-subtle">Remind me in 3 weeks to re-check — readiness only means something as a trend.</span>
                    </label>
                    {emailErr && <div className="mt-3 text-sm text-danger-700" data-testid="rc-email-error">{emailErr}</div>}
                    <button data-testid="rc-email-submit" onClick={sendResult} disabled={sending} className="pp-btn-dark w-full mt-4 disabled:opacity-60">{sending ? "Sending…" : "Send my result"}</button>
                    <p className="text-xs text-muted-foreground mt-3">One email with your result. The reminder only if you tick the box. Unsubscribe anytime. See our <Link to="/privacy" className="underline">Privacy policy</Link>.</p>
                  </>
                )}
              </div>
              <div className="pp-card p-6 border-ink">
                <div className="font-head text-lg font-extrabold">Keep this result</div>
                <p className="text-sm text-subtle mt-1">Create a free account to track this over time and run the full 25-question assessment. This result becomes your first baseline.</p>
                <Link to="/login?mode=register&carry=1" data-testid="rc-save-cta" className="pp-btn-dark mt-4">Start your 30-day trial — no card needed <ArrowRight size={15} /></Link>
              </div>
            </>
          )}
        </div>
      </section>
    </MarketingShell>
  );
}
